import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for detecting exit intent (mouse leaving viewport)
 * @param {number} sensitivity - Distance from top before triggering (px)
 * @param {number} delay - Delay before popup can show again (ms)
 * @returns {Array} [showPopup boolean, dismissPopup function]
 */
export const useExitIntent = (sensitivity = 50, delay = 300000) => {
  const [showPopup, setShowPopup] = useState(() => {
    if (typeof window === 'undefined') return false;
    let hasStoredEmail = false;
    try { hasStoredEmail = !!window.localStorage.getItem('userEmail'); } catch (_) {}
    return sessionStorage.getItem('exitIntentOpen') === 'true'
      && sessionStorage.getItem('isPremiumUser') !== 'true'
      && !hasStoredEmail;
  });
  const [hasShown, setHasShown] = useState(() => {
    // Check if popup was shown in this session or user is already premium
    if (typeof window === 'undefined') return false;
    if (sessionStorage.getItem('isPremiumUser') === 'true') {
      return true;
    }
    // Also skip exit intent entirely for logged-in users
    try {
      if (window.localStorage && window.localStorage.getItem('userEmail')) {
        return true;
      }
    } catch (_) {
      // ignore storage access issues
    }
    return sessionStorage.getItem('exitIntentShown') === 'true';
  });

  const dismissPopup = useCallback(() => {
    setShowPopup(false);
    sessionStorage.removeItem('exitIntentOpen');
    sessionStorage.setItem('exitIntentShown', 'true');
    setHasShown(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const isPremium = sessionStorage.getItem('isPremiumUser') === 'true';
    let isLoggedIn = false;
    try {
      isLoggedIn = !!(window.localStorage && window.localStorage.getItem('userEmail'));
    } catch (_) {
      isLoggedIn = false;
    }

    // Don't attach listener if already shown, user is premium, or logged in
    if (hasShown || isPremium || isLoggedIn) return;

    const show = () => {
      if (document.querySelector('.cookie-consent-overlay')) return;
      sessionStorage.setItem('exitIntentOpen', 'true');
      setShowPopup(true);
      sessionStorage.setItem('exitIntentShown', 'true');
      setHasShown(true);
    };

    const handleMouseLeave = (e) => {
      // Only trigger if mouse is leaving from top of viewport
      if (e.type === 'mouseout' && e.relatedTarget) return;
      if (e.clientY <= sensitivity && !showPopup) {
        show();
      }
    };

    if (window.matchMedia?.('(pointer: coarse)').matches) {
      // Touch devices have no mouse-leave event. Offer once after an engaged visit.
      let ready = false;
      let eligibleAt = Number(sessionStorage.getItem('exitIntentEligibleAt'));
      if (!Number.isFinite(eligibleAt) || eligibleAt <= 0) {
        eligibleAt = Date.now() + 20000;
        sessionStorage.setItem('exitIntentEligibleAt', String(eligibleAt));
      }
      const onScroll = () => {
        if (ready && window.scrollY >= window.innerHeight / 2) show();
      };
      const timer = setTimeout(() => {
        ready = true;
        onScroll();
      }, Math.max(0, eligibleAt - Date.now()));
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
      };
    }

    document.addEventListener('mouseleave', handleMouseLeave);
    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
      document.removeEventListener('mouseout', handleMouseLeave);
    };
  }, [hasShown, showPopup, sensitivity]);

  return [showPopup, dismissPopup];
};

export default useExitIntent;
